import React from "react";
import { useEffect, useRef } from "react";
import { useMemo } from "react";
import { useState } from "react";
import { patchReq } from "../../../../apis/internalApi";
import { classNames } from "../../../../helpers/classNames";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import useArray from "../../../../hooks/useArray";
import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid'


function SelectBox({ options, handleChange = () => { } }) {
  const [query, setQuery] = useState('')

  const [selectedOption, setSelectedOption] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) => {
        return option.toLowerCase().includes(query.toLowerCase())
      })
  useEffect(() => {
    if (!isInitialized) return setIsInitialized(true)
    if (!selectedOption) return
    handleChange(selectedOption)

  }, [selectedOption])


  return (
    <Combobox as="div" value={selectedOption} onChange={setSelectedOption}>
      <div className="">

        <Combobox.Button className="invisible group-hover:visible text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-sm h-6 w-6 p-1">
          <PlusIcon className="" />
        </Combobox.Button>

        {true && (
          <Combobox.Options className="absolute top-full z-40 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            <Combobox.Input
              placeholder="Search for an option..."
              className="w-full border-0 bg-white py-1.5 pl-3 pr-10  text-gray-900 ring-0  text-sm sm:leading-6"
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(option) => option}
            />
            {filteredOptions.length > 0 && filteredOptions.map((option) => (
              <Combobox.Option
                key={option}
                value={option}
                className={({ active }) =>
                  classNames(
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    <span className={classNames('block truncate', selected && 'font-semibold')}>{option}</span>

                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          active ? 'text-white' : 'text-blue-600'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  )
}

const MultiSelect = ({
  rowId,
  initValue,
  accessToken,
  table,
  colId,
  editable,
  selectOptions,
  editId,
  onCellChange
}) => {
  const selected = useArray(initValue || []);
  const [showExpanded, setShowExpanded] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const multiSelectContainerRef = useRef(null);
  const panelRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStartOffset({
      x: e.clientX - panelPosition.x,
      y: e.clientY - panelPosition.y,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPanelPosition({
        x: e.clientX - dragStartOffset.x,
        y: e.clientY - dragStartOffset.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStartOffset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target) &&
        multiSelectContainerRef.current && !multiSelectContainerRef.current.contains(event.target)) {

        if (JSON.stringify(initValue || []) !== JSON.stringify(selected.array)) {
          onCellChange(rowId, colId, selected.array);
        }
        setShowExpanded(false);
      }
    };

    if (showExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExpanded, selected.array, initValue, onCellChange, rowId, colId]);

  const openPanel = () => {
    if (multiSelectContainerRef.current) {
      setPanelPosition({
        x: multiSelectContainerRef.current.offsetLeft + multiSelectContainerRef.current.offsetWidth + 5,
        y: multiSelectContainerRef.current.offsetTop,
      });
    }
    setShowExpanded(true);
  };

  useEffect(() => {
    selected.set(initValue || []);
  }, [initValue]);

  //handling next thing changes
  const saveChanges = async (newValue) => {
    try {
      const results = await patchReq(table, "smart_table_update", accessToken, {
        value: newValue,
        row_id: editId,
        col_id: colId,
      });

      // Trigger cell update acknowledgment
      if (onCellChange) {
        onCellChange(rowId, colId, newValue);
      }
    } catch (error) { }
  };

  const sortedOptions = useMemo(
    () => selectOptions.sort((a, b) => a.order - b.order),
    [selectOptions]
  );

  const displayText = useMemo(() => {
    if (!selected.array || selected.array.length === 0) return "";
    if (selected.array.length <= 3) {
      return selected.array.join(", ");
    }
    return `${selected.array.length} items selected`;
  }, [selected.array]);

  return (
    <>
      <div
        ref={multiSelectContainerRef}
        className="group cursor-pointer border-2 border-transparent hover:border-blue-500 rounded-md transition-all duration-150 ease-in-out px-2 py-1 h-full flex items-center w-full"
        onClick={openPanel}
      >
        <p className="text-left truncate w-full">
          {displayText}
        </p>
      </div>

      {showExpanded && (
        <div
          ref={panelRef}
          style={{
            position: "absolute",
            zIndex: 1000,
            left: `${panelPosition.x}px`,
            top: `${panelPosition.y}px`,
            cursor: isDragging ? "grabbing" : "default",
          }}
          className="bg-gray-100 shadow-md rounded-lg"
        >
          <div className="py-5 px-7 sm:px-9 sm:py-7">
            <div
              className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200"
              style={{ cursor: "grab" }}
              onMouseDown={handleMouseDown}
            >
              <h3 className="text-lg font-medium text-gray-900 select-none">
                Edit Selection
              </h3>
              <button
                onClick={() => {
                  if (JSON.stringify(initValue || []) !== JSON.stringify(selected.array)) {
                    onCellChange(rowId, colId, selected.array);
                  }
                  setShowExpanded(false);
                }}
                className="text-gray-400 hover:text-gray-600"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 overflow-y-auto max-h-[70vh] w-[400px]">
              {editable ? (
                <div className="bg-white p-3 rounded-md shadow-sm">
                  <div className="group w-full flex flex-col space-y-3">
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-gray-300 rounded-md">
                      {selected.array && selected.array.map((item, index) => (
                        <span
                          key={item}
                          className="bg-blue-100 text-blue-800 space-x-1 flex items-center font-normal rounded-full px-3 py-1 text-sm"
                        >
                          <span>{item}</span>
                          <button
                            onClick={() => selected.remove(index)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <XIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      {(!selected.array || selected.array.length === 0) && (
                        <span className="text-gray-400 text-sm">No items selected</span>
                      )}
                    </div>

                    <SelectBox
                      options={sortedOptions.flatMap(item => item.value).filter(item => !selected.array.includes(item))}
                      handleChange={(value) => selected.push(value)}
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-white p-3 rounded-md shadow-sm">
                  <div className="flex flex-wrap gap-2">
                    {selected.array && selected.array.map((item, index) => (
                      <span
                        key={item}
                        className="bg-gray-200 text-gray-700 font-normal rounded-full px-3 py-1 text-sm"
                      >
                        {item}
                      </span>
                    ))}
                    {(!selected.array || selected.array.length === 0) && (
                      <span className="text-gray-400 text-sm">No items selected</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MultiSelect;
